import { ArrowUpRight, ChevronDown } from 'lucide-react';
import { Plate } from '@/components/landing/primitives';
import { stagger } from '@/components/landing/stagger';
import { BENEFITS, LAYERS, PILLARS, STACKS } from '@/components/landing/inference-layers';

/**
 * The centerpiece image, rebuilt as text. Every label baked into the picture
 * is here as real DOM, so crawlers, screen readers and translators get what
 * the pixels say; the hero image's `aria-describedby` points at this lead.
 *
 * The rows say it in plain English for the business reader. The image's
 * jargon lives in one closed <details> at the end ("for your IT team") —
 * still in the DOM for crawlers, out of the way for everyone else.
 */
export function HowSection() {
  return (
    <Plate eyebrow="The AGICAFET AI Inference Layer" id="how" labelledBy="how-title">
      <h2 id="how-title" className="lp-h2 lp-reveal max-w-4xl" style={stagger(1)}>
        One system, three layers
      </h2>
      <p id="how-lead" className="lp-body lp-reveal mt-6 max-w-2xl" style={stagger(2)}>
        Every AGICAFET system has the same three layers. You use the top one. We build, tune and run the two
        below it, so it stays local, fast, private, and hybrid when you need it.
      </p>

      <ol className="mt-16 border-b border-border md:mt-20">
        {LAYERS.map((layer, i) => (
          <li key={layer.id} className="lp-reveal layer-row" style={stagger(3 + i)}>
            <span className="layer-n" aria-hidden>
              {layer.n}
            </span>

            <div>
              <h3 className="t-h3">{layer.name}</h3>
              <p className="eyebrow mt-3">{layer.imageName}</p>
            </div>

            <div>
              <p className="text-[17px] leading-relaxed text-[hsl(var(--fg-secondary))] md:text-lg">{layer.line}</p>
              {layer.id === 'compute' && (
                <p className="mt-4 text-[15px] text-muted-foreground">
                  It runs in three places:{' '}
                  {PILLARS.map((p, j) => (
                    <span key={p.id}>
                      {j > 0 && ', '}
                      <a href={`#${p.id}`} className="text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground">
                        {p.name}
                      </a>
                    </span>
                  ))}
                  .
                </p>
              )}
            </div>

            <dl className="layer-gets">
              {layer.gets.map((id) => (
                <div key={id}>
                  <dt>{BENEFITS[id].label}</dt>
                  <dd>{BENEFITS[id].gloss}</dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ol>

      <details className="tech-details lp-reveal mt-10" style={stagger(6)}>
        <summary>
          Technical detail for your IT team <ChevronDown className="tech-chevron h-4 w-4" aria-hidden />
        </summary>
        <dl className="mt-6 grid gap-5 md:grid-cols-[14rem_1fr] md:gap-x-10">
          {LAYERS.map((layer) => (
            <div key={layer.id} className="contents">
              <dt className="text-[15px] font-medium text-foreground">{layer.imageName}</dt>
              <dd className="layer-terms">{layer.terms.join(' · ')}</dd>
            </div>
          ))}
          {STACKS.map((s) => (
            <div key={s.id} className="contents">
              <dt className="text-[15px] font-medium text-foreground">{s.name}</dt>
              <dd className="layer-terms">{s.terms.join(' · ')}</dd>
            </div>
          ))}
        </dl>
        <a
          href="/img/inference-layer/full-2560.webp"
          target="_blank"
          rel="noopener noreferrer"
          className="link-quiet mt-6"
        >
          See the full diagram <ArrowUpRight className="h-4 w-4" />
        </a>
      </details>
    </Plate>
  );
}
